/**
 * Vehicle cascade selector (RAZ-327 homepage finder, RAZ-337 header panel).
 *
 * One Alpine component drives every copy of snippets/rb-vehicle-selector-fields.liquid:
 * Make → Model → Year → Variant, where Variant is generation and variant in one select —
 * the generation appears only as its group headings, in the years a model straddles two
 * of them. A Build date select follows for the few vehicles whose destination changes
 * part-way through a year, and nobody else ever sees it.
 *
 * It owns the cascade and nothing else. It never navigates, never decides what a CTA
 * does and never saves a vehicle — the host block or section reads `vehicle`,
 * `destination` and `isComplete` and makes those calls itself.
 *
 * Every list of options and every "what is left to ask" answer comes from
 * src/js/utils/vehicleResolver.js. Nothing here re-derives cascade or fitment logic,
 * so the finder, the header panel, the PDP card and the migration's verify step can
 * never disagree about what a selection means.
 *
 * Failure is survivable: a catalogue that will not load leaves `hasError` true, the
 * selects disabled and the host free to hide itself or show its own notice.
 */

import { loadVehicleData } from '../utils/vehicleData.js';
import {
  IMPOSSIBLE,
  buildVehicle,
  candidates as candidatesFor,
  destinationUrl as destinationUrlFor,
  nextStep,
  optionsFor,
  parseTrimValue,
  resolveDestination,
} from '../utils/vehicleResolver.js';

/** Window event fired after every settled change, for analytics and host hosts. */
export const SELECTION_CHANGED_EVENT = 'arc:action:vehicleSelectionChanged';

/** Resolver level to the field key that holds its answer. They are the same words. */
const FIELD_BY_LEVEL = {
  make: 'make',
  model: 'model',
  year: 'year',
  trim: 'trim',
  band: 'band',
};

/** Field keys in cascade order, so clearing below a level is one slice. */
const FIELD_ORDER = ['make', 'model', 'year', 'trim', 'band'];

/**
 * Query parameters the finder round-trips, mapped to seed keys. `series`, `variant`,
 * `body` and `qualifier` are read as well as the packed `trim`, so a link written for
 * the old Generation-then-Variant cascade still lands on the right option.
 */
const QUERY_KEYS = ['make', 'model', 'year', 'trim', 'series', 'variant', 'body', 'qualifier', 'fitment'];

/** Screen-reader copy, overridable through setFieldLabels(). */
const DEFAULT_STATUS = {
  loading: 'Loading the vehicle list…',
  error: 'The vehicle list is unavailable right now.',
  prompt: 'Choose your {field} to continue.',
  ready: 'Your vehicle is ready.',
  noMatch: 'No vehicles match that combination. Change a field above.',
};

/** Field names used in the status message until the snippet supplies its own. */
const DEFAULT_FIELD_LABELS = {
  make: 'make',
  model: 'model',
  year: 'year',
  trim: 'variant',
  body: 'body type',
  band: 'build date',
};

/** The shape optionsFor() returns when there is nothing to ask. */
const EMPTY_FIELD = { options: [], groups: null, isBodyOnly: false, isRemoved: true, isAutoSelect: false };

/**
 * Case-insensitive comparison of two option values.
 * @param {*} left
 * @param {*} right
 * @returns {boolean}
 */
function isSameValue(left, right) {
  if (left === null || left === undefined || right === null || right === undefined) return false;
  return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
}

/**
 * First non-blank value across the seed sources, in priority order.
 * @param {Array<Object>} sources
 * @param {string} key
 * @returns {string|null}
 */
function firstValue(sources, key) {
  for (const source of sources) {
    const value = source?.[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') return String(value).trim();
  }
  return null;
}

/**
 * The one debug warning this component emits, guarded so production consoles stay quiet.
 * @param {string} message
 * @param {*} detail
 */
function warnOnce(message, detail) {
  if (typeof window !== 'undefined' && window.enableDebugMode) { console.warn(message, detail); }
}

export default {
  name: 'vehicleSelector',

  /**
   * @param {Object} [params] host configuration, see the contract in the block that renders it
   * @param {string} [params.make] seed handle, wins over the query string and the saved vehicle
   * @param {string} [params.model] seed handle
   * @param {string|number} [params.year] seed build year, the third question
   * @param {string} [params.trim] seed packed generation / variant option value
   * @param {string} [params.series] seed generation handle, narrows the variant seed
   * @param {string} [params.variant] seed variant name
   * @param {string} [params.body] seed body type, narrows an ambiguous variant seed
   * @param {string} [params.qualifier] seed seat or trim qualifier
   * @param {string|number} [params.fitment] seed fitment id, the Build date step's answer
   * @param {boolean} [params.enableQueryString] read ?make=&model=… (default true)
   * @param {boolean} [params.enableSavedVehicle] seed from $store.vehicle (default false)
   * @param {Function} [params.onChange] called with the change detail after every change
   * @returns {Object}
   */
  component(params = {}) {
    const config = params && typeof params === 'object' ? params : {};

    return {
      /* -------------------------------------------------------------- */
      /* State                                                          */
      /* -------------------------------------------------------------- */

      /** Indexed catalogue from loadVehicleData(), or null. */
      data: null,

      /** True while the catalogue request is in flight. */
      isLoading: true,

      /** True once the catalogue is indexed and the selects can be used. */
      isReady: false,

      /** True when the catalogue could not be loaded; the host degrades. */
      hasError: false,

      /**
       * Raw select values, all strings. Bound with x-model by the snippet.
       * `year` is the build year; `trim` is the packed generation / variant value; `band`
       * is the fitment id the Build date step answers with.
       */
      fields: { make: '', model: '', year: '', trim: '', band: '' },

      /** Levels this component filled in by itself because they had one option. */
      autoSelected: {},

      /** Increments on every settled change, so a host can $watch it. */
      changeCount: 0,

      /** Host callback, assignable after construction as well as through params. */
      onChange: typeof config.onChange === 'function' ? config.onChange : null,

      /** Field names and status copy the snippet hands over for the live region. */
      fieldLabels: { ...DEFAULT_FIELD_LABELS },
      statusCopy: { ...DEFAULT_STATUS },

      /* -------------------------------------------------------------- */
      /* Lifecycle                                                      */
      /* -------------------------------------------------------------- */

      async init() {
        try {
          const data = await loadVehicleData();
          this.applyData(data);
        } catch (error) {
          this.isLoading = false;
          this.isReady = false;
          this.hasError = true;
          warnOnce('vehicleSelector: the vehicle catalogue could not be loaded.', error);
        }
      },

      /**
       * Adopt an indexed catalogue, seed the selection and settle the cascade.
       * Split out of init() so the unit tests can drive the component without a
       * network, a DOM or Alpine.
       *
       * @param {Object} data index from indexData()
       */
      applyData(data) {
        this.data = data ?? null;
        this.isLoading = false;
        this.isReady = Boolean(this.data);
        this.hasError = !this.isReady;
        if (!this.isReady) return;

        this.reconcileSavedVehicle();
        this.seed();
        this.advance();
        this.announce('init');
      },

      /**
       * Let the saved-vehicle store re-resolve itself against this catalogue, but only
       * when the host opted into the store: on a page with no vehicle surface the
       * store is nobody's business but its own.
       */
      reconcileSavedVehicle() {
        if (config.enableSavedVehicle !== true) return;
        try {
          this.$store?.vehicle?.refresh(this.data);
        } catch (error) {
          warnOnce('vehicleSelector: the saved vehicle could not be reconciled.', error);
        }
      },

      /* -------------------------------------------------------------- */
      /* Seeding                                                        */
      /* -------------------------------------------------------------- */

      /**
       * The seed sources, highest priority first: explicit params, then the query
       * string, then the saved vehicle when the host asked for it.
       *
       * The saved vehicle only fills a finder nothing else has named a vehicle for. A link
       * describes the vehicle the shopper is after, and filling its gaps from a different
       * saved vehicle opened a Ranger link as the saved HiLux's year, or answered a linked
       * Prado's Build date with the saved one's (RAZ-327 QA, TC-F14). The block's pre-paint
       * script reserves the card's height by the same rule.
       * @returns {Array<Object>}
       */
      seedSources() {
        const sources = [config];
        if (config.enableQueryString !== false) sources.push(this.querySeed());
        const isVehicleNamed = sources.some((source) => QUERY_KEYS.some((key) => firstValue([source], key) !== null));
        if (config.enableSavedVehicle === true && !isVehicleNamed) sources.push(this.savedVehicleSeed());
        return sources.filter(Boolean);
      },

      /**
       * ?make=&model=&year=&trim= — the round trip the finder block and the vehicle hub
       * link with. The old ?series=&variant= spelling is still read.
       * @returns {Object}
       */
      querySeed() {
        const seed = {};
        try {
          const search = new URLSearchParams(window.location?.search ?? '');
          QUERY_KEYS.forEach((key) => {
            const value = search.get(key);
            if (value) seed[key] = value;
          });
        } catch {
          // No location on this page; an empty seed is correct.
        }
        return seed;
      },

      /**
       * The saved vehicle, flattened into seed fields.
       * @returns {Object}
       */
      savedVehicleSeed() {
        try {
          const saved = this.$store?.vehicle?.vehicle ?? null;
          if (!saved) return {};
          // A vehicle saved before the year-first cascade has no year, so the walk stops
          // at the Year select and the shopper answers it once — the variant cannot be
          // offered until the year has narrowed the list.
          return {
            make: saved.make?.h ?? null,
            model: saved.model?.h ?? null,
            year: saved.year ?? null,
            series: saved.series?.h ?? null,
            body: saved.body ?? null,
            variant: saved.variant ?? null,
            qualifier: saved.qualifier ?? null,
            fitment: saved.complete === true ? saved.id ?? null : null,
          };
        } catch (error) {
          warnOnce('vehicleSelector: the saved vehicle could not be read.', error);
          return {};
        }
      },

      /**
       * Fill the fields from the seed sources, level by level. A value the catalogue
       * does not know stops the walk, so an unknown make never drags a stale model in
       * behind it.
       */
      seed() {
        const sources = this.seedSources();
        let isBroken = false;

        // Each level settles the cascade before the next is read, so a level with one
        // answer is filled in on the way past, exactly as it would be for a shopper.
        const applyLevel = (field, list) => {
          if (isBroken) return;
          const wanted = firstValue(sources, field);
          if (wanted !== null) {
            const match = list().find((option) => isSameValue(option.value, wanted));
            if (!match) {
              isBroken = true;
              return;
            }
            this.fields[field] = match.value;
          }
          this.advance();
        };

        applyLevel('make', () => this.makeOptions);
        applyLevel('model', () => this.modelOptions);
        applyLevel('year', () => this.yearOptions);
        if (isBroken) return;

        const trimSeed = {
          value: firstValue(sources, 'trim'),
          series: firstValue(sources, 'series'),
          variant: firstValue(sources, 'variant'),
          body: firstValue(sources, 'body'),
          qualifier: firstValue(sources, 'qualifier'),
        };
        if (trimSeed.value !== null || trimSeed.variant !== null || trimSeed.body !== null) {
          const match = this.matchTrimSeed(trimSeed);
          if (!match) return;
          this.fields.trim = match.value;
          this.advance();
        }

        // The Build date step answers with a fitment id, so it round-trips under its own
        // parameter rather than `year`, which is the shopper's build year.
        const fitment = firstValue(sources, 'fitment');
        if (fitment === null) return;
        const bandMatch = this.bandOptions.find((option) => isSameValue(option.value, fitment));
        if (bandMatch) this.fields.band = bandMatch.value;
      },

      /**
       * The generation / variant option a seed names: an exact packed value first, then
       * variant plus body, qualifier and generation, then the variant alone, then the
       * body alone. The generation only ever narrows — a link naming a series that the
       * chosen year does not have still finds the variant.
       *
       * @param {{value: string|null, series: string|null, variant: string|null,
       *          body: string|null, qualifier: string|null}} seed
       * @returns {Object|null}
       */
      matchTrimSeed(seed) {
        const options = this.trimOptions;
        const exact = options.find((option) => isSameValue(option.value, seed.value));
        if (exact) return exact;

        const wanted = seed.variant;
        const fits = (option, withSeries) =>
          (wanted === null || isSameValue(option.variant, wanted)) &&
          (seed.body === null || isSameValue(option.body, seed.body)) &&
          (seed.qualifier === null || isSameValue(option.qualifier, seed.qualifier)) &&
          (!withSeries || seed.series === null || isSameValue(option.series, seed.series));

        return (
          options.find((option) => fits(option, true)) ??
          options.find((option) => fits(option, false)) ??
          (wanted !== null ? options.find((option) => isSameValue(option.variant, wanted)) : null) ??
          options.find((option) => isSameValue(option.body, seed.body ?? wanted)) ??
          null
        );
      },

      /* -------------------------------------------------------------- */
      /* Selection                                                      */
      /* -------------------------------------------------------------- */

      /**
       * The fields as the resolver reads them: an unanswered level is absent, and an
       * answered generation / variant is unpacked into series, body, variant and
       * qualifier. The year is not in here — the resolver takes it as its own argument.
       * @returns {Object}
       */
      get selection() {
        const chosen = {};
        if (this.fields.make) chosen.make = this.fields.make;
        if (this.fields.model) chosen.model = this.fields.model;
        if (this.fields.trim) {
          const parts = parseTrimValue(this.fields.trim);
          chosen.series = parts.series;
          chosen.body = parts.body;
          chosen.variant = parts.variant;
          chosen.qualifier = parts.qualifier;
        }
        if (this.fields.band) chosen.fitmentId = this.fields.band;
        return chosen;
      },

      /** The chosen build year as a number, or null while it is unanswered. */
      get knownYear() {
        if (!this.fields.year) return null;
        const year = Number(this.fields.year);
        return Number.isFinite(year) ? year : null;
      },

      /** Fitments still possible for the current selection. */
      get candidates() {
        if (!this.isReady) return [];
        return candidatesFor(this.data, this.selection, this.knownYear);
      },

      /* -------------------------------------------------------------- */
      /* Options — every list comes from the resolver                   */
      /* -------------------------------------------------------------- */

      get makeOptions() {
        if (!this.isReady) return [];
        return this.data.makes.map((make) => ({ value: make.handle, label: make.name }));
      },

      /**
       * Models are never narrowed by the year: the year sits below the model now, so a
       * shopper who has picked 2019 can still change the model without clearing it first.
       */
      get modelOptions() {
        if (!this.isReady) return [];
        return optionsFor('model', this.data, this.selection, null).options;
      },

      /** Every year the chosen model covers, newest first. Never narrowed by a year. */
      get yearOptions() {
        if (!this.isReady || !this.fields.model) return [];
        return optionsFor('year', this.data, this.selection, null).options;
      },

      /**
       * The resolver's whole generation / variant answer: flat options, the groups (body
       * types, or generations in a changeover year) and the "no candidate carries a
       * variant" flag that renames the field.
       *
       * Offered only once the year is answered. Before that every fitment the model has
       * is a candidate, so the list would carry every generation's variants at once — the
       * question the year exists to spare the shopper.
       *
       * @returns {Object}
       */
      get trimField() {
        if (!this.isReady || !this.fields.model) return EMPTY_FIELD;
        if (this.hasYearStep && !this.fields.year) return EMPTY_FIELD;
        return optionsFor('trim', this.data, this.selection, this.knownYear);
      },

      /** Every generation / variant option, flattened out of its groups. Always complete. */
      get trimOptions() {
        return this.trimField.options;
      },

      /**
       * The <optgroup>s, or [] when there is nothing worth a heading.
       *
       * A lone group keeps its heading when it names something: GWM Ute's single "Dual
       * Cab" tells the shopper which body the variants belong to, and the design draws it
       * (TC-F08). A group with no label — a body-only list, or one generation's variants
       * with no body — stays a flat list.
       */
      get trimGroups() {
        const groups = this.trimField.groups ?? [];
        const isHeaded = groups.length > 1 || (groups.length === 1 && Boolean(groups[0].label));
        return isHeaded ? groups : [];
      },

      /** The flat list the snippet renders, empty when the groups are rendered instead. */
      get trimFlatOptions() {
        return this.isTrimGrouped ? [] : this.trimOptions;
      },

      get isTrimGrouped() {
        return this.trimGroups.length > 0;
      },

      /** True when no candidate carries a variant, so the field is "Body type". */
      get isTrimBodyOnly() {
        return this.trimField.isBodyOnly === true;
      },

      /**
       * The Build date step, and only once every step above it has been answered.
       *
       * Without that guard a model's year bands would be offered straight after the
       * model — nine of them reading "2015 – now" for a HiLux, indistinguishable from
       * each other — and picking one resolves a complete vehicle, variant and all, that
       * the shopper never described. The resolver is not at fault: asked about a
       * selection that stops at the model, every fitment the model has is still a
       * candidate. Only the component knows which questions are still unanswered.
       *
       * @returns {Array<Object>}
       */
      get bandOptions() {
        if (!this.isReady || !this.fields.model) return [];
        if (this.hasYearStep && !this.fields.year) return [];
        if (this.hasTrimStep && !this.fields.trim) return [];
        return optionsFor('band', this.data, this.selection, this.knownYear).options;
      },

      /**
       * The flat option list for one level, by its resolver name.
       * @param {'make'|'model'|'year'|'trim'|'band'} level
       * @returns {Array<Object>}
       */
      optionsByLevel(level) {
        if (level === 'make') return this.makeOptions;
        if (level === 'model') return this.modelOptions;
        if (level === 'year') return this.yearOptions;
        if (level === 'trim') return this.trimOptions;
        if (level === 'band') return this.bandOptions;
        return [];
      },

      /* -------------------------------------------------------------- */
      /* Which fields exist, and which are usable                       */
      /* -------------------------------------------------------------- */

      get hasYearStep() {
        return this.yearOptions.length > 0;
      },

      get hasTrimStep() {
        return this.trimOptions.length > 0;
      },

      get hasBandStep() {
        return this.bandOptions.length > 0;
      },

      /**
       * Should the field be in the layout at all? Make, Model and Year always are; the
       * Variant field appears while it still has something to ask, so a model like the
       * Kia EV5 — one fitment, nothing below the year — shows three fields rather than a
       * dead one.
       *
       * While the catalogue is still in flight nothing is removed. This component used
       * to answer "make and model only" until the data landed, which collapsed a card
       * that had already painted four fields down to two and then grew it back again —
       * the flash the finder card was reported for. An unanswerable field is disabled,
       * not absent, so the card keeps the height it painted with. Build date is the one
       * exception: it exists for a handful of mid-year facelifts and would otherwise
       * reserve a field almost nobody ever sees.
       *
       * @param {'make'|'model'|'year'|'trim'|'band'} level
       * @returns {boolean}
       */
      isLevelShown(level) {
        if (!this.isReady) return level !== 'band';
        if (level === 'make' || level === 'model') return true;
        if (level === 'year') return !this.fields.model || this.hasYearStep;
        if (level === 'trim') {
          if (!this.fields.model) return true;
          if (this.hasYearStep && !this.fields.year) return true;
          return this.hasTrimStep;
        }
        if (level === 'band') return this.hasBandStep;
        return false;
      },

      /**
       * A field is disabled while the catalogue is loading, while a field above it is
       * unanswered, or when it has no options to offer.
       *
       * @param {'make'|'model'|'year'|'trim'|'band'} level
       * @returns {boolean}
       */
      isLevelDisabled(level) {
        if (!this.isReady) return true;
        if (level === 'make') return this.makeOptions.length === 0;
        if (level === 'model') return !this.fields.make || this.modelOptions.length === 0;
        if (level === 'year') return !this.fields.model || !this.hasYearStep;
        if (level === 'trim') {
          if (!this.fields.model) return true;
          if (this.hasYearStep && !this.fields.year) return true;
          return !this.hasTrimStep;
        }
        if (level === 'band') return !this.hasBandStep;
        return true;
      },

      /* -------------------------------------------------------------- */
      /* Helper copy the resolver carries on its options                */
      /* -------------------------------------------------------------- */

      /**
       * The facelift note, e.g. "Facelift arrived Sep 2021, check your build plate".
       * Shown as soon as the Build date step appears, not only once it is answered —
       * it is the thing that tells the shopper which option to pick.
       */
      get bandHelperText() {
        const chosen = this.bandOptions.find((option) => option.value === this.fields.band);
        return chosen?.note ?? this.bandOptions.find((option) => option.note)?.note ?? '';
      },

      /** The chosen model's own "shop all" collection, for the host's exit link. */
      get modelCollection() {
        const chosen = this.modelOptions.find((option) => option.value === this.fields.model);
        return chosen?.collection ?? null;
      },

      /** "Toyota HiLux" and friends, for the host's copy. */
      get modelLabel() {
        const chosen = this.modelOptions.find((option) => option.value === this.fields.model);
        return chosen?.label ?? '';
      },

      /* -------------------------------------------------------------- */
      /* What is left to ask                                            */
      /* -------------------------------------------------------------- */

      /** The resolver's next question: a level, IMPOSSIBLE, or null when settled. */
      get nextLevel() {
        if (!this.isReady) return null;
        return nextStep(this.data, this.selection, this.knownYear);
      },

      /** True when the selection matches no fitment at all. */
      get isImpossible() {
        return this.nextLevel === IMPOSSIBLE;
      },

      /** True when every field this vehicle has is answered. */
      get isComplete() {
        return this.isReady && Boolean(this.fields.model) && this.nextLevel === null;
      },

      /** True once at least one field is answered. */
      get hasSelection() {
        return FIELD_ORDER.some((field) => Boolean(this.fields[field]));
      },

      /** The destination every candidate shares, or null when they disagree. */
      get destination() {
        if (!this.isReady || this.isImpossible) return null;
        return resolveDestination(this.candidates);
      },

      /** "/collections/toyota-hilux-8th-gen-seat-covers?filter-…", or null. */
      get destinationHref() {
        return destinationUrlFor(this.destination);
      },

      get hasDestination() {
        return this.destination !== null;
      },

      /** The saved-vehicle object for this selection, or null. The host saves it. */
      get vehicle() {
        if (!this.isReady || !this.fields.model) return null;
        return buildVehicle(this.data, this.selection, this.knownYear, this.candidates);
      },

      /* -------------------------------------------------------------- */
      /* Changing                                                       */
      /* -------------------------------------------------------------- */

      /**
       * Wired to every select's @change. x-model has already written the value, so this
       * only has to clear the levels below it and settle the cascade again.
       *
       * @param {'make'|'model'|'year'|'trim'|'band'} level
       */
      onFieldChange(level) {
        this.clearBelow(level);
        this.advance();
        this.announce('change');
      },

      /**
       * Set one level programmatically and run the same cascade a shopper would.
       * @param {'make'|'model'|'year'|'trim'|'band'} level
       * @param {string} value
       */
      select(level, value) {
        const field = FIELD_BY_LEVEL[level];
        if (!field) return;
        this.fields[field] = value === null || value === undefined ? '' : String(value);
        this.onFieldChange(level);
      },

      /**
       * Empty every field below a level, and forget that this component filled any of
       * them in by itself. The level's own auto-selected flag goes too: whatever it
       * holds now, a shopper put it there.
       *
       * @param {'make'|'model'|'year'|'trim'|'band'} level
       */
      clearBelow(level) {
        const field = FIELD_BY_LEVEL[level];
        const index = FIELD_ORDER.indexOf(field);
        if (index === -1) return;
        FIELD_ORDER.slice(index).forEach((key) => {
          if (key !== field) this.fields[key] = '';
        });
        Object.keys(FIELD_BY_LEVEL).forEach((name) => {
          if (FIELD_ORDER.indexOf(FIELD_BY_LEVEL[name]) >= index) delete this.autoSelected[name];
        });
      },

      /**
       * Answer every question that has only one answer, so the shopper is never asked
       * to pick from a list of one. Silent by design: the run is observable through
       * autoSelectedLevels rather than through an event per step.
       */
      advance() {
        for (let guard = 0; guard < FIELD_ORDER.length + 1; guard += 1) {
          const level = this.nextLevel;
          if (!level || level === IMPOSSIBLE) return;
          const options = this.optionsByLevel(level);
          if (options.length !== 1) return;
          this.fields[FIELD_BY_LEVEL[level]] = options[0].value;
          this.autoSelected[level] = true;
        }
      },

      /**
       * Re-assert a select's value once its options exist.
       *
       * x-model binds the select before the x-for inside it has rendered a single
       * option, so a value this component fills in by itself — a seed, or an
       * auto-selected level — would be dropped on the floor and the field would sit
       * there showing the placeholder while the cascade believed it was answered.
       * The snippet calls this from x-effect, one select at a time.
       *
       * @param {Object} element the <select>
       * @param {string} value the field value it should be showing
       */
      syncSelect(element, value) {
        if (!element) return;
        const wanted = value === null || value === undefined ? '' : String(value);
        const apply = () => {
          if (element.value !== wanted) element.value = wanted;
        };
        if (typeof this.$nextTick === 'function') this.$nextTick(apply);
        else apply();
      },

      /** Levels this component answered by itself, in cascade order. */
      get autoSelectedLevels() {
        return Object.keys(FIELD_BY_LEVEL).filter((level) => this.autoSelected[level] === true);
      },

      /**
       * Was this level filled in automatically rather than chosen?
       * @param {'make'|'model'|'year'|'trim'|'band'} level
       * @returns {boolean}
       */
      isAutoSelected(level) {
        return this.autoSelected[level] === true;
      },

      /** Empty every field, the year included, and settle again. */
      reset() {
        FIELD_ORDER.forEach((field) => { this.fields[field] = ''; });
        this.autoSelected = {};
        this.advance();
        this.announce('reset');
      },

      /* -------------------------------------------------------------- */
      /* Telling the host                                               */
      /* -------------------------------------------------------------- */

      /** Everything a host needs to react to a change, without reading the cascade. */
      get changeDetail() {
        return {
          fields: { ...this.fields },
          selection: this.selection,
          knownYear: this.knownYear,
          nextLevel: this.nextLevel,
          isComplete: this.isComplete,
          isImpossible: this.isImpossible,
          destination: this.destination,
          destinationHref: this.destinationHref,
          vehicle: this.vehicle,
          autoSelectedLevels: this.autoSelectedLevels,
        };
      },

      /**
       * Bump the watchable counter, call the host's callback and fire the window event.
       * Called once per settled change, after auto-advance, never once per step.
       *
       * @param {'init'|'change'|'reset'} reason
       */
      announce(reason) {
        this.changeCount += 1;
        const detail = { reason, ...this.changeDetail };

        if (typeof this.onChange === 'function') {
          try {
            this.onChange(detail);
          } catch (error) {
            warnOnce('vehicleSelector: the host onChange callback failed.', error);
          }
        }

        try {
          window.dispatchEvent(new CustomEvent(SELECTION_CHANGED_EVENT, { detail }));
        } catch (error) {
          warnOnce(`vehicleSelector: ${SELECTION_CHANGED_EVENT} could not be dispatched.`, error);
        }
      },

      /* -------------------------------------------------------------- */
      /* Screen-reader status                                           */
      /* -------------------------------------------------------------- */

      /**
       * The snippet hands over the merchant's own field names and status copy, so the
       * live region speaks the same language as the visible labels.
       *
       * @param {Object} labels {make, model, year, trim, body, band}
       * @param {Object} [copy] {loading, error, prompt, ready, noMatch}
       */
      setFieldLabels(labels, copy) {
        Object.entries(labels ?? {}).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim() !== '') this.fieldLabels[key] = value.trim();
        });
        Object.entries(copy ?? {}).forEach(([key, value]) => {
          if (typeof value === 'string' && value.trim() !== '') this.statusCopy[key] = value.trim();
        });
      },

      /** The visible name of a level, with Variant renaming itself to Body type. */
      labelForLevel(level) {
        if (level === 'trim' && this.isTrimBodyOnly) return this.fieldLabels.body;
        return this.fieldLabels[level] ?? level;
      },

      /** What the aria-live region says. Empty once there is nothing to report. */
      get statusMessage() {
        if (this.hasError) return this.statusCopy.error;
        if (this.isLoading) return this.statusCopy.loading;
        if (this.isImpossible) return this.statusCopy.noMatch;
        if (!this.hasSelection) return '';
        if (this.isComplete) return this.statusCopy.ready;
        const level = this.nextLevel;
        if (!level) return '';
        return this.statusCopy.prompt.replace('{field}', this.labelForLevel(level));
      },
    };
  },
};
